/**
 * Memory sync / unsync API (Step Function Memory Manager)
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function authHeaders(idToken) {
  const t = idToken || getRestApiBearerToken();
  return {
    Authorization: `Bearer ${t}`,
    'Content-Type': 'application/json'
  };
}

/**
 * POST /networks/{networkId}/agents/{agentId}/memory/sync
 * @param {string} networkId
 * @param {string} agentId - logicalId e.g. SalesAgent (URL-encoded)
 * @param {{ s3Key: string, layer: 'hot'|'cold', documentId: string, connectionId?: string }} body
 * @returns {Promise<{ taskId: string, status: string, message?: string }>}
 */
export async function syncMemory(networkId, agentId, body, idToken) {
  const enc = encodeURIComponent(agentId);
  const res = await fetch(`${API_BASE_URL}/networks/${networkId}/agents/${enc}/memory/sync`, {
    method: 'POST',
    headers: authHeaders(idToken),
    body: JSON.stringify({
      s3Key: body.s3Key,
      layer: body.layer,
      documentId: body.documentId,
      ...(body.connectionId ? { connectionId: body.connectionId } : {}),
      ...(body.tenantId != null && body.tenantId !== '' ? { tenantId: body.tenantId } : {}),
      ...(body.customerId != null && body.customerId !== '' ? { customerId: body.customerId } : {})
    })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || res.statusText || 'Sync failed');
  }
  return data;
}

/**
 * DELETE /networks/{networkId}/agents/{agentId}/memory/sync/{documentId}?layer=hot|cold
 */
export async function unsyncMemory(networkId, agentId, documentId, layer, idToken, connectionId, extras = {}) {
  const encAgent = encodeURIComponent(agentId);
  const encDoc = encodeURIComponent(documentId);
  const params = new URLSearchParams({ layer });
  if (connectionId) params.set('connectionId', connectionId);
  if (extras.tenantId != null && extras.tenantId !== '') params.set('tenantId', extras.tenantId);
  if (extras.customerId != null && extras.customerId !== '') params.set('customerId', extras.customerId);
  const res = await fetch(
    `${API_BASE_URL}/networks/${networkId}/agents/${encAgent}/memory/sync/${encDoc}?${params}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${idToken}` }
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || res.statusText || 'Unsync failed');
  }
  return data;
}
